import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Link } from "react-router-dom";
import { IndianRupee, ShoppingCart, AlertTriangle, CalendarClock, PawPrint, Users, Boxes, TrendingUp, MessageCircle } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, hint, accent }) => (
  <Card className="p-5 border-border hover:-translate-y-0.5 transition-transform" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-start justify-between">
      <div className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">{label}</div>
      <div className={`w-9 h-9 rounded-lg grid place-items-center ${accent || "bg-muted text-foreground"}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <div className="mt-3 font-heading text-3xl sm:text-4xl font-black tracking-tighter">{value}</div>
    {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
  </Card>
);

const PnlRow = ({ label, value, color }) => {
  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  const tone = color === "rose" ? "text-rose-700" : "text-emerald-700";
  return (
    <div className="rounded-lg border border-border p-3" data-testid={`pnl-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-heading text-xl font-bold ${tone}`}>{inr(value)}</div>
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(r => setStats(r.data));
  }, []);

  if (!stats) return <div className="text-muted-foreground">Loading dashboard…</div>;

  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Overview</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-1">Today at the Store</h1>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex h-2 w-2 rounded-full bg-green-500" />
          WhatsApp: <span className="font-semibold uppercase">{stats.whatsapp_mode}</span>
        </div>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <StatCard icon={IndianRupee} label="Today Sales" value={inr(stats.today_sales)} hint={`${stats.today_invoices_count} invoices`} accent="bg-primary/10 text-primary" />
        <StatCard icon={TrendingUp} label="Month Sales" value={inr(stats.month_sales)} hint="See P&L below" />
        <StatCard icon={CalendarClock} label="Today Appointments" value={stats.today_appointments_count} />
        <StatCard icon={AlertTriangle} label="Low Stock" value={stats.low_stock_count} hint="Re-order soon" accent="bg-orange-100 text-orange-700" />
        <StatCard icon={Users} label="Customers" value={stats.customers_count} />
        <StatCard icon={PawPrint} label="Pets" value={stats.pets_count} />
        <StatCard icon={Boxes} label="Products" value={stats.products_count} />
        <StatCard icon={ShoppingCart} label="Walk-in" value={<Link to="/pos" className="text-primary hover:underline">Open POS</Link>} hint="Fast billing" />
      </div>

      {/* P&L breakdown */}
      <Card className="p-5" data-testid="dashboard-pnl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">P&amp;L this month</div>
            <div className="font-heading text-xl font-semibold mt-1">Revenue by stream · Expenses · Net</div>
          </div>
          <Link to="/financing" className="text-sm text-primary font-semibold hover:underline">Open Financing →</Link>
        </div>
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
          <PnlRow label="Products" value={stats.product_sales_month} color="emerald" />
          <PnlRow label="Grooming" value={stats.grooming_sales_month} color="emerald" />
          <PnlRow label="Boarding" value={stats.boarding_sales_month} color="emerald" />
          <PnlRow label="Services" value={stats.service_sales_month} color="emerald" />
          <PnlRow label="Custom" value={stats.custom_sales_month} color="emerald" />
          <PnlRow label="Expenses" value={-stats.expenses_month} color="rose" />
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-border p-3">
            <div className="text-xs text-muted-foreground">Parlour expenses (cost of services)</div>
            <div className="font-heading text-xl font-bold text-rose-700">{inr(stats.parlour_expense_month)}</div>
          </div>
          <div className={`rounded-lg border p-3 ${stats.net_pnl_month >= 0 ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}>
            <div className="text-xs text-muted-foreground">Net P&amp;L (sales − expenses)</div>
            <div className={`font-heading text-2xl font-bold ${stats.net_pnl_month >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{inr(stats.net_pnl_month)}</div>
          </div>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">Today's appointments</div>
              <div className="font-heading text-xl font-semibold mt-1">Schedule</div>
            </div>
            <Link to="/appointments" className="text-sm text-primary font-semibold hover:underline">View all →</Link>
          </div>
          <div className="mt-4 divide-y divide-border" data-testid="dashboard-appointments">
            {stats.today_appointments.length === 0 && <div className="text-sm text-muted-foreground py-4">No appointments today.</div>}
            {stats.today_appointments.map(a => (
              <div key={a.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{new Date(a.slot_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                  <div className="text-xs text-muted-foreground">Status: {a.status}</div>
                </div>
                <div className="text-sm text-muted-foreground">Pet ID: {a.pet_id?.slice(0,8)}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">Low stock alerts</div>
              <div className="font-heading text-xl font-semibold mt-1">Re-order</div>
            </div>
            <Link to="/products?low=1" className="text-sm text-primary font-semibold hover:underline">Manage →</Link>
          </div>
          <div className="mt-4 divide-y divide-border" data-testid="dashboard-low-stock">
            {stats.low_stock_items.length === 0 && <div className="text-sm text-muted-foreground py-4">All stocked up.</div>}
            {stats.low_stock_items.map(p => (
              <div key={p.id} className="py-3 flex items-center justify-between">
                <div className="text-sm font-medium">{p.name}</div>
                <div className="text-xs"><span className="text-orange-600 font-semibold">{p.stock}</span> / {p.threshold}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">Expiring soon</div>
              <div className="font-heading text-xl font-semibold mt-1">Next 30 days</div>
            </div>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dashboard-expiring">
            {stats.expiring_soon.length === 0 && <div className="text-sm text-muted-foreground">No batches expiring soon.</div>}
            {stats.expiring_soon.map(b => (
              <div key={b.id} className="border border-border rounded-lg p-3">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Batch {b.batch_no}</div>
                <div className="font-semibold text-sm mt-1">Expires {b.expiry_date}</div>
                <div className="text-xs text-muted-foreground mt-1">Qty: {b.quantity}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
